<template>
  <div class="container py-4">
    <div class="row justify-content-center">
     <div class="col-md-8 b-3">

        <div class="page-bar py-3">
          <div class="d-flex align-items-center">
            <BackButton class="btn btn-link p-0 pl-2 mr-2" fallback="/host">
              <i class="fas fa-chevron-left"></i>
            </BackButton>
            <div class="page-title flex-grow-1">
              Guests
            </div>
 
                  <button @click="showAddGuestModal" class="btn btn-primary btn-sm btn-add btn-compact px-3 py-1 mr-2 ml-auto">
                    <i class="fas fa-user-plus mr-1"></i>
                    <span class="btn-label">Add Guest</span>
                  </button>
          </div>
        </div>

     </div>

      <div class="col-md-8">
        <div class="card shadow-sm">
          <div class="card-body p-0">
            <div class="container mt-3 mb-5 p-1">
              <!-- Header + Save -->
              <div class="mb-2 d-flex align-items-center justify-content-right"> 
                <div class="d-flex align-items-center gap-2">
                  <button
                    :disabled="!hasPendingChanges || saving"
                    @click="saveGuests"
                    class="btn btn-success btn-sm px-3 py-1 btn-compact"
                    :title="hasPendingChanges ? 'Save additions/removals' : 'No changes to save'"
                  >
                    <span v-if="!saving">
                      <i class="fas fa-save mr-1"></i>
                      <span class="btn-label">Save</span>
                      <span class="btn-label d-none d-sm-inline">
                        <span v-if="addsCount || removesCount">
                          (Add {{ addsCount }} · Remove {{ removesCount }})
                        </span>
                      </span>
                    </span>
                    <span v-else>
                      <span class="spinner-border spinner-border-sm mr-1"></span>
                      <span class="btn-label">Saving…</span>
                    </span>
                  </button>

                </div>
              </div>

              <!-- Selected chips -->
              <div v-if="selectedDetails.length" class="chips-wrap-block mb-3">
                <div class="chips-header d-flex align-items-center justify-content-between">
                  <div class="chips-title">
                    Selected ({{ selectedDetails.length }})
                  </div>
                  <button class="btn btn-link p-0 small" @click="clearSelected" title="Clear selected">
                    Clear selected
                  </button>
                </div>

                <div class="chips-wrap">
                  <div class="chip" v-for="g in selectedDetails" :key="g.uid">
                    <img v-no-right-click v-if="g.user_img" :src="g.user_img" class="chip-avatar" :alt="displayName(g)" />
                    <div v-else class="chip-avatar chip-initials">{{ safeInitials(g) }}</div>
                    <span class="chip-label" :title="displayName(g)">{{ displayName(g) }}</span>
                    <button class="chip-remove" @click="toggleSelect(g)" aria-label="Remove">&times;</button>
                  </div>
                </div>
              </div>

              <!-- Search -->
              <div class="input-group mb-3">
                <div class="input-group-prepend">
                  <span class="input-group-text"><i class="fas fa-search"></i></span>
                </div>
                <input
                  type="text"
                  class="form-control"
                  placeholder="Search guests by name, username, email or phone..."
                  v-model="searchQuery"
                />
              </div>

              <!-- List -->
              <div class="card">
                <div class="card-body p-0">
                  <!-- Loading -->
                  <div v-if="loading" class="text-center p-4">
                    <div class="spinner-border text-primary" role="status">
                      <span class="sr-only">Loading...</span>
                    </div>
                  </div>

                  <!-- Empty -->
                  <div v-else-if="filteredGuests.length === 0" class="text-center p-4">
                    <p class="text-muted">No guests found</p>
                  </div>

                  <!-- Items -->
                  <div v-else>
                    <ul class="list-group" role="list">
                      <li
                        v-for="(guest, i) in filteredGuests"
                        :key="guest?.uid || i"
                        class="guest position-relative"
                        role="listitem"
                        :class="{ 'is-selected': isSelected(guest) }"
                      >
                        <!-- Avatar -->
                        <img
                          class="avatar"
                          v-if="guest?.user_img"
                          :src="guest.user_img"
                          :alt="displayName(guest)"
                        />
                        <img
                          class="avatar"
                          v-else
                          :src="`https://dummyimage.com/200x200/222/fff.png?text=${safeInitials(guest)}`"
                          :alt="displayName(guest)"
                        />

                        <!-- Identity + right actions -->
                        <div class="flex-grow-1">
                          <div class="guest-line">
                            <div class="min-w-0 pr-2">
                              <p class="name mr-2 mb-0 text-truncate">
                                {{ displayName(guest) }}
                                <span v-if="isCoHost(guest)" class="badge badge-warning ml-2">Co-host</span>
                              </p>
                              <div
                                class="contact-chips"
                                :class="{ 'chips-scroll': guest?.email && guest?.phone && !guest?.username }"
                              >
                                <template v-if="hasUsername(guest)">
                                  <span class="chip-contact chip-username">
                                    <i class="fas fa-at mr-1"></i>{{ guest.username }}
                                  </span>
                                </template>
                                <template v-else>
                                  <span v-if="guest?.email" class="chip-contact chip-email">
                                    <i class="fas fa-envelope mr-1"></i>{{ guest.email }}
                                  </span>
                                  <span v-if="guest?.phone" class="chip-contact chip-phone">
                                    <i class="fas fa-phone mr-1"></i> {{ guest?.code || guest?.phone_code }} {{ guest.phone }}
                                  </span>
                                </template>
                              </div>
                            </div>

                            <div class="guest-actions">
                              <!-- Select / Check only when NOT a co-host -->
                              <div
                                class="actions d-flex align-items-center flex-shrink-0"
                                v-if="!isCoHost(guest)"
                              >
                                <button
                                  class="btn btn-sm px-3 py-1 btn-selectable mr-2"
                                  :class="isSelected(guest) ? 'btn-primary active' : 'btn-outline-primary'"
                                  role="button"
                                  :aria-pressed="isSelected(guest)"
                                  @click="toggleSelect(guest)"
                                  @keydown.enter.prevent="toggleSelect(guest)"
                                  @keydown.space.prevent="toggleSelect(guest)"
                                  :title="isSelected(guest) ? 'Unselect' : 'Select'"
                                >
                                  <i :class="isSelected(guest) ? 'fas fa-check' : 'fas fa-user-plus'"></i>
                                </button>
                              </div>

                              <!-- Row dropdown only when NOT a co-host (and allowed to edit/delete) -->
                              <div
                                v-if="!isCoHost(guest) && guest?.edit_delete_option === 1"
                                class="row-dropdown"
                                :class="{ show: openRowId === guest?.uid }"
                              >
                                <button
                                  class="btn btn-light p-2 rounded-circle row-menu-btn"
                                  type="button"
                                  :aria-expanded="openRowId === guest?.uid ? 'true' : 'false'"
                                  @click.stop="toggleRowMenu(guest?.uid)"
                                  title="Actions"
                                >
                                  <i class="fas fa-ellipsis-v"></i>
                                </button>
                                <div class="dropdown-menu dropdown-menu-right">
                                  <a class="dropdown-item" href="javascript:void(0)" @click.prevent="onEditFromMenu(guest)">
                                    <i class="fas fa-edit mr-2"></i>Edit
                                  </a>
                                  <a class="dropdown-item text-danger" href="javascript:void(0)" @click.prevent="onDeleteFromMenu(guest)">
                                    <i class="fas fa-trash mr-2"></i>Delete
                                  </a>
                                </div>
                              </div>
                              <div v-else>

                                <button
                                  class="btn p-2 rounded-circle row-menu-btn bg-transparent"
                                  type="button" 
                                  title="Actions"
                                >
                                  <i class="fas fa-ellipsis-v d-none"></i>
                                </button>
                              </div>
                              
                              <!-- If co-host: hide actions entirely -->
                            </div>

                          </div>
                        </div>
                      </li>
                    </ul>
                  </div>
                  <!-- /Items -->
                </div>
              </div>
              <!-- /List -->
            </div>
          </div>
        </div>
      </div>
    </div>

    <ContactModal
      v-model="showContactModal"
      v-model:contact="currentGuest"
      :error="guestFormError"
      :loading="saving"
      :title="isEditing ? 'Edit Guest' : 'Add Guest'"
      :submit-label="isEditing ? 'Update' : 'Add Guest'"
      @submit="saveGuest"
      @cancel="closeContactModal"
    />

    <!-- Delete Confirmation Modal -->
    <div class="modal fade" id="deleteModal" tabindex="-1" role="dialog" aria-labelledby="deleteModalLabel" aria-hidden="true" ref="deleteModal">
      <div class="modal-dialog" role="document">
        <div class="modal-content">
          <div class="modal-header">
            <h5 id="deleteModalLabel" class="modal-title">Confirm Delete</h5>
            <button type="button" class="close" @click="hideModal(deleteModal)" aria-label="Close">
              <span aria-hidden="true">&times;</span>
            </button>
          </div>
          <div class="modal-body">
            <p>Remove <strong>{{ currentGuest.name }}</strong> from contacts?</p>
          </div>
          <div class="modal-footer px-0">
            <button type="button" class="btn btn-secondary" @click="hideModal(deleteModal)">Cancel</button>
            <button type="button" class="btn btn-danger" @click="deleteGuest">Delete</button>
          </div>
        </div>
      </div>
    </div>

  </div>
</template>

<script>
import { ref, reactive, computed, onMounted, onUnmounted, nextTick, watch, watchEffect } from "vue";
import { useRoute } from "vue-router";
import api from "@/services/api";
import { useSessionStore } from "@/stores/session";
import BackButton from '@/components/common/BackButton.vue'
import ContactModal from '@/components/common/ContactModal.vue'

import Swal from "sweetalert2";
import "sweetalert2/dist/sweetalert2.min.css";

export default {
  name: "GuestList",
  components: { BackButton, ContactModal },
  setup() {
    const route = useRoute();
    const session = useSessionStore();

    const isAlive = ref(true);

    const toast = (icon, title) =>
      Swal.fire({
        toast: true,
        position: "top-end",
        icon,
        title,
        showConfirmButton: false,
        timer: 2000,
        timerProgressBar: true,
      });

    const deriveEventId = (r) => {
      const cand = [r.params?.eventid, r.params?.id, r.params?.eventId, r.query?.eventid, r.query?.id, r.query?.eventId]
        .find(v => v != null && String(v).trim() !== "");
      if (cand) return String(cand);
      const lastSeg = (r.path || r.fullPath || "").split("?")[0].split("/").filter(Boolean).pop();
      return lastSeg && /^\d+$/.test(lastSeg) ? lastSeg : null;
    };
    const eventId = ref(deriveEventId(route));

    watch(
      () => route.fullPath,
      async () => {
        if (!isAlive.value) return;
        eventId.value = deriveEventId(route);
        await Promise.all([loadInvitationDetail(), loadGuests()]);
      },
      { flush: "post" }
    );

    const guests = ref([]);
    const loading = ref(true);
    const saving  = ref(false);
    const searchQuery = ref("");
    const isEditing = ref(false);

    // event type from InvitationDetail(): 'new' | 'old'
    const eventType = ref("new");

    // Selection (in-memory only)
    const selectedUids = ref([]);
    // Original invited as returned from API (based on add_status)
    const originalInvitedUids = ref(new Set());

    const isGuest = (g) => !!g && typeof g === "object";
    const hasUsername = (g) => {
      const u = g?.username
      return !!(u && String(u).trim())
    }
    const displayName = (g) => (g && (g.name || g.username || g.email || g.phone)) || "Guest";
    const safeInitials = (g) => {
      const n = g && (g.name || g.username || g.email || "G");
      return (String(n).trim().split(" ")[0] || "G").slice(0, 1).toUpperCase();
    };

    const selectedSet = computed(() => new Set(selectedUids.value));

    // --- Normalizers & helpers
    const isCoHost = (g) => {
      const v = (g?.co_host ?? g?.cohost ?? g?.is_cohost ?? g?.is_co_host ?? "").toString().toLowerCase().trim();
      return v === "yes" || v === "true" || v === "1";
    };

    const isInvited = (g) => {
      if (!g) return false;
      const raw =
        g.add_status ??
        g.add_Status ??
        g.addStatus ??
        g.invited ??
        g.invited_status ??
        g.is_added ??
        0;
      return raw === true || raw === 1 || raw === "1";
    };

    // Filter & sort
    const filteredGuests = computed(() => {
      const base = guests.value.filter(isGuest);
      const q = (searchQuery.value || "").toLowerCase();

      const filtered = q
        ? base.filter((g) => {
            const name = String(g.name || "").toLowerCase();
            const username = String(g.username || "").toLowerCase();
            const email = String(g.email || "").toLowerCase();
            const phone = String(g.phone || "").toLowerCase();
            return name.includes(q) || username.includes(q) || email.includes(q) || phone.includes(q);
          })
        : base;

      // Sort: selected first, then by name
      return filtered
        .slice()
        .sort((a, b) => {
          const aSel = selectedSet.value.has(a.uid) ? 1 : 0;
          const bSel = selectedSet.value.has(b.uid) ? 1 : 0;
          if (aSel !== bSel) return bSel - aSel;
          return String(a.name || "").localeCompare(String(b.name || ""));
        });
    });

    const selectedDetails = computed(() =>
      guests.value.filter((g) => isGuest(g) && selectedSet.value.has(g.uid))
    );

    // Diff for Save (lists of guest objects)
    const adds = computed(() =>
      guests.value.filter((g) => isGuest(g) && selectedSet.value.has(g.uid) && !originalInvitedUids.value.has(g.uid))
    );
    const removes = computed(() =>
      guests.value
        .filter((g) => isGuest(g) && originalInvitedUids.value.has(g.uid) && !selectedSet.value.has(g.uid))
        .filter((g) => g.edit_delete_option === 1) // only if allowed to remove
    );
    const addsCount = computed(() => adds.value.length);
    const removesCount = computed(() => removes.value.length);
    
    // Detect any change (additions or removals)
    const hasPendingChanges = computed(() => {
      const currentSet = new Set(selectedUids.value);
      const hasAdditions = selectedUids.value.some(uid => !originalInvitedUids.value.has(uid));
      const hasRemovals = Array.from(originalInvitedUids.value).some(uid => !currentSet.has(uid));
      return hasAdditions || hasRemovals;
    });

    // Add/Edit modal submit guard (ESLint "used" satisfied via watchEffect)
    const currentGuest = reactive({
      id: null,
      uid: null,
      name: "",
      email: "",
      phone_code: "",
      phone: "",
      username: "",
      contact_id: null,
    });
    const canSubmitGuest = computed(() => {
      const nameOk = !!currentGuest.name?.trim();
      const emailOk = !!currentGuest.email?.trim();
      const phoneOk = !!currentGuest.phone?.trim();
      return nameOk && (emailOk || phoneOk);
    });
    watchEffect(() => { void canSubmitGuest.value; });

    // Select / Deselect
    function isSelected(guest) {
      if (!isGuest(guest)) return false;
      return selectedSet.value.has(guest.uid);
    }
    function toggleSelect(guest) {
      if (!isGuest(guest) || isCoHost(guest)) return; // no selection for co-host
      const uid = guest.uid;
      const next = [...selectedUids.value];
      const idx = next.indexOf(uid);
      if (idx > -1) next.splice(idx, 1);
      else next.push(uid);
      selectedUids.value = next;
    }
    function clearSelected() {
      selectedUids.value = [];
    }

    // ---- Load Invitation Detail (event_type) ----
    async function loadInvitationDetail() {
      try {
        if (!eventId.value) return;

        const fd = new FormData();
        fd.append("userid", session.userId);
        fd.append("eventid", eventId.value);

        const res = await api.getInvitationDetail(fd);

        // Success check
        const ok = res?.data?.status === "ok" || res?.status === true;
        if (ok) {
          // Prefer root-level event_type, fallback to eventsinfo[0]
          const rootType = res.data?.event_type;
          const info = (res.data?.eventsinfo && res.data.eventsinfo.length > 0)
            ? res.data.eventsinfo[0]
            : {};

          const t = (rootType ?? info.event_type ?? "new").toString().toLowerCase();
          eventType.value = t === "old" ? "old" : "new";

          // Optional: if you want to store the whole event info for other usage
          // eventDetail.value = info;
        } else {
          eventType.value = "new";
        }
      } catch (e) {
        console.error("InvitationDetail failed:", e);
        eventType.value = "new";
      }
    }


    async function loadGuests() {
      try {
        if (!eventId.value) return;
        loading.value = true;
        const fd = new FormData();
        fd.append("userid", session.userId);
        fd.append("eventid", eventId.value);

        const response = await api.getGuestsList(fd);
        const ok = response?.data?.status === "ok";

        if (ok) {
          const list =
            (response.data?.data && Array.isArray(response.data.data))
              ? response.data.data
              : (Array.isArray(response.data) ? response.data : []);

          const next = list
            .filter((g) => g != null)
            .map((g, idx) => {
              const normalized = typeof g === "object" ? g : {};

              const rawAdd =
                normalized.add_status ??
                normalized.add_Status ??
                normalized.addStatus ??
                normalized.invited ??
                normalized.invited_status ??
                normalized.is_added ??
                0;
              const invitedFlag = rawAdd === true || rawAdd === 1 || rawAdd === "1";

              return {
                ...normalized,
                id: normalized.id ?? null,
                invited: invitedFlag,
                add_status: invitedFlag ? 1 : 0,
                co_host: (normalized.co_host ?? normalized.cohost ?? normalized.is_cohost ?? "").toString(),
                edit_delete_option: Number(normalized.edit_delete_option ?? normalized.can_edit ?? 0),
                phone_code: normalized.code || normalized.phone_code || "",
                contact_id: normalized.contact_id ?? null,
                uid: `ev:${eventId.value}:id:${normalized.id ?? idx}`,
              };
            });

          guests.value = next;

          // Preselect invited ones
          selectedUids.value = next.filter(g => isInvited(g)).map(g => g.uid);
          // Freeze the baseline (for diffing)
          originalInvitedUids.value = new Set(selectedUids.value);
        } else {
          guests.value = [];
          selectedUids.value = [];
          originalInvitedUids.value = new Set();
        }
      } catch (e) {
        console.error("Error loading guests:", e);
        guests.value = [];
        selectedUids.value = [];
        originalInvitedUids.value = new Set();
      } finally {
        loading.value = false;
      }
    }

    // Phone utils
    function sanitizePhone() {
      currentGuest.phone = (currentGuest.phone || "").replace(/\D/g, "").slice(0, 10);
    }

    // Modals (no jQuery fallback)
    const showContactModal = ref(false);
    const deleteModal = ref(null);
    const closeContactModal = () => { showContactModal.value = false; };

    const showModal = (elRef) => {
      if (!isAlive.value) return;
      const el = elRef?.value || elRef;
      if (!el) return;
      if (window.$?.fn?.modal) window.$(el).modal("show");
      else {
        el.classList.add("show");
        el.style.display = "block";
        el.removeAttribute("aria-hidden");
        el.setAttribute("aria-modal", "true");
        const backdrop = document.createElement("div");
        backdrop.className = "modal-backdrop fade show";
        backdrop.dataset.source = el.id || "modal";
        document.body.appendChild(backdrop);
        document.body.classList.add("modal-open");
      }
    };

    const hideModal = (elRef) => {
      const el = elRef?.value || elRef;
      if (!el) return;
      if (window.$?.fn?.modal) window.$(el).modal("hide");
      else {
        el.classList.remove("show");
        el.style.display = "none";
        el.setAttribute("aria-hidden", "true");
        el.removeAttribute("aria-modal");
        const source = el.id || "modal";
        [...document.querySelectorAll(".modal-backdrop")]
          .filter(b => b.dataset.source === source)
          .forEach(b => b.remove());
        if (!document.querySelector(".modal.show")) {
          document.body.classList.remove("modal-open");
        }
      }
    };

    const guestFormError = ref("");

    onMounted(async () => {
      await Promise.all([loadInvitationDetail(), loadGuests()]);
      await nextTick();
      if (!isAlive.value) return;
      document.addEventListener("click", closeRowMenu);
    });

    onUnmounted(() => {
      isAlive.value = false;
      document.removeEventListener("click", closeRowMenu);
    });

    // Row menu
    const openRowId = ref(null);
    const toggleRowMenu = (uid) => { openRowId.value = openRowId.value === uid ? null : uid; };
    const closeRowMenu = () => { openRowId.value = null; };

    // Modal actions
    async function showAddGuestModal() {
      isEditing.value = false;
      resetCurrentGuest();
      guestFormError.value = "";
      await nextTick();
      if (!isAlive.value) return;
      if (!currentGuest.phone_code) currentGuest.phone_code = "+91";
      showContactModal.value = true;
    }

    async function editGuest(guest) {
      if (!isGuest(guest)) return;
      isEditing.value = true;
      guestFormError.value = "";
      Object.assign(currentGuest, {
        id: guest.id,
        uid: guest.uid,
        name: guest.name || "",
        email: guest.email || "",
        phone_code: guest.phone_code || guest.code || "",
        phone: guest.phone || "",
        username: guest.username || "",
        contact_id: guest.contact_id ?? null,
      });
      await nextTick();
      if (!isAlive.value) return;
      if (!currentGuest.phone_code) currentGuest.phone_code = "+91";
      showContactModal.value = true;
    }

    function validateGuest() {
      guestFormError.value = "";
      if (!currentGuest.name?.trim()) {
        guestFormError.value = "Name is required.";
        return false;
      }
      const hasEmail = !!currentGuest.email?.trim();
      const hasPhone = !!currentGuest.phone?.trim();
      if (!hasEmail && !hasPhone) {
        guestFormError.value = "Provide at least one contact: email or phone.";
        return false;
      }
      if (hasEmail && !/^\S+@\S+\.\S+$/.test(currentGuest.email)) {
        guestFormError.value = "Please enter a valid email address.";
        return false;
      }
      if (hasPhone && currentGuest.phone.length !== 10) {
        guestFormError.value = "Phone must be 10 digits.";
        return false;
      }
      return true;
    }

    async function saveGuest(payload) {
      try {
        if (payload) Object.assign(currentGuest, payload)
        if (!validateGuest()) {
          toast("warning", guestFormError.value || "Please fix the form.");
          return;
        }

        let response;
        if (isEditing.value) {
          const fd = new FormData();
          fd.append("userid", session.userId);
          fd.append("eventid", eventId.value);
          fd.append("contact_id", currentGuest.contact_id);
          fd.append("name", currentGuest.name);
          fd.append("email", currentGuest.email || "");
          fd.append("phone", currentGuest.phone || "");
          fd.append("phone_code", currentGuest.phone_code || "");
          response = await api.editGuest(fd);
        } else {
          const fd = new FormData();
          fd.append("userid", session.userId);
          fd.append("eventid", eventId.value);
          fd.append("name", currentGuest.name);
          fd.append("email", currentGuest.email || "");
          fd.append("phone", currentGuest.phone || "");
          fd.append("code", currentGuest.phone_code || "");
          response = await api.addGuestNew(fd);
        }

        const ok = response?.status === true || response?.data?.status === true || response?.data?.status === "ok";
        if (ok) {
          showContactModal.value = false;
          toast("success", isEditing.value ? "Guest updated" : "Guest added");
          await loadGuests();
        } else if (response?.message || response?.data?.message) {
          guestFormError.value = response.message || response.data.message;
          toast("error", guestFormError.value);
        } else {
          toast("error", "Unable to save guest.");
        }
      } catch (e) {
        console.error("Error saving guest:", e);
        toast("error", "Something went wrong. Please try again.");
      }
    }

    function confirmDelete(guest) {
      if (!isGuest(guest)) return;
      Object.assign(currentGuest, guest);
      showModal(deleteModal);
    }

    async function deleteGuest() {
      try {
        const fd = new FormData();
        fd.append("userid", session.userId);
        fd.append("eventid", eventId.value);
        if (currentGuest.contact_id) fd.append("contact_id", currentGuest.contact_id);
        else if (currentGuest.id) fd.append("guestid", currentGuest.id);

        const response = await api.deleteGuest(fd);
        const ok = response?.status === true || response?.data?.status === true || response?.data?.status === "ok";
        if (ok) {
          hideModal(deleteModal);
          toast("success", "Guest deleted");
          await loadGuests();
        } else {
          toast("error", "Delete failed");
        }
      } catch (e) {
        console.error("Error deleting guest:", e);
        toast("error", "Delete failed");
      }
    }

    // Save = send all currently selected guests
    // Switch API by event_type: 'new' -> addGuest, 'old' -> addGuestAuto
    async function saveGuests() {
      if (!hasPendingChanges.value || saving.value) return;
      try {
        saving.value = true;

        const allSelectedGuests = guests.value.filter(g => isGuest(g) && selectedSet.value.has(g.uid));

        const fd = new FormData();
        fd.append("userid", session.userId);
        fd.append("eventid", eventId.value);
        allSelectedGuests.forEach((g) => {
          fd.append("names[]", g.name || "");
          fd.append("emails[]", g.email || "");
          fd.append("phones[]", g.phone || "");
          fd.append("codes[]", g.phone_code || g.code || "");
          if (g.id) fd.append("guest_ids[]", g.id);
          if (g.contact_id) fd.append("contact_ids[]", g.contact_id);
        });

        // choose API
        console.log(eventType.value);
        const apiCall = eventType.value === "old" ? api.addGuestAuto : api.addGuest;
        const response = await apiCall(fd);

        const ok = response?.status === true || response?.data?.status === true || response?.data?.status === "ok";
        if (ok) {
          originalInvitedUids.value = new Set(selectedUids.value);
          toast("success", "Guests saved successfully");
          await loadGuests();
        } else {
          toast("error", "Failed to save guests");
        }
      } catch (e) {
        console.error("Error saving guests:", e);
        toast("error", "Failed to save changes");
      } finally {
        saving.value = false;
      }
    }

    function resetCurrentGuest() {
      currentGuest.id = null;
      currentGuest.uid = null;
      currentGuest.name = "";
      currentGuest.email = "";
      currentGuest.phone_code = "";
      currentGuest.phone = "";
      currentGuest.username = "";
      currentGuest.contact_id = null;
    }

    return {
      // state
      guests,
      loading,
      saving,
      searchQuery,
      isEditing,
      currentGuest,
      guestFormError,
      closeContactModal,

      // selection (no localStorage)
      selectedUids,

      // row dropdown
      openRowId,
      toggleRowMenu,
      onEditFromMenu: (g) => { closeRowMenu(); editGuest(g); },
      onDeleteFromMenu: (g) => { closeRowMenu(); confirmDelete(g); },

      // refs
      showContactModal,
      deleteModal,

      // computed
      filteredGuests,
      selectedDetails,
      addsCount,
      removesCount,
      hasPendingChanges,
      canSubmitGuest,

      // methods
      displayName,
      hasUsername,
      safeInitials,
      isCoHost,
      isInvited,
      isSelected,
      toggleSelect,
      clearSelected,
      showAddGuestModal,
      editGuest,
      saveGuest,
      confirmDelete,
      deleteGuest,
      saveGuests,
      sanitizePhone,
      hideModal,
    };
  },
};
</script>

<style>
:root{ --brand-primary:#0069d9; }

.guest{ display:flex;align-items:center; padding:.8rem .5rem;border-bottom:1px solid rgba(0,0,0,.06); }
.guest:last-child{border-bottom:0;}
.guest.is-selected{ background:#f7fbff; border-left:3px solid var(--brand-primary); }
.avatar{ width:42px;height:42px;border-radius:50%;object-fit:cover;margin-right:.75rem; box-shadow:0 1px 5px rgba(0,0,0,.1); }
.guest .name{font-weight:600; margin-bottom:0;}
.guest .handle{color:#888; font-size:.9rem;}
.guest .meta{font-size:.9rem; color:#888; margin-left:8px;}
@media (max-width: 767px){
  .guest .name{font-size:.98rem;}
  .guest .handle,.guest .meta{font-size:.85rem;}
}
.btn-add{font-weight:600;}
.btn.btn-sm{ line-height:1.2; }
.btn-selectable{
  border-width:2px;
  transition:transform .06s ease, box-shadow .12s ease;
  width:36px; height:36px;
  border-radius:50%;
  padding:0;
  display:inline-flex; align-items:center; justify-content:center;
}
.btn-selectable.active { box-shadow:0 0 0 0.2rem rgba(0,105,217,.15); }
.actions { white-space: nowrap; }
.min-w-0 { min-width: 0; }
.text-truncate { overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
.btn-label{display:inline;}
.btn-compact i{margin-right:.25rem;}
/* guest row layout */
.guest-line{display:grid;grid-template-columns:1fr auto;align-items:center;gap:.5rem;}
.guest-actions{display:flex;gap:.45rem;align-items:center;flex-wrap:nowrap;justify-content:flex-end;margin-left:auto;}
@media (max-width: 575px){
  .btn-add{padding:.45rem .6rem;}
  .btn-add .btn-label{display:none;}
  .btn-add i{margin-right:0 !important;}
  .btn-compact{padding:.45rem .65rem;}
  .btn-compact .btn-label{display:none;}
  .btn-compact i{margin-right:0 !important;}
  .guest-line{grid-template-columns:1fr auto;}
  .guest-actions{justify-content:flex-end;}
  .guest .name{font-size:.95rem;}
  .guest .handle,.guest .meta{font-size:.8rem;}
}
.btn-add .btn-label{display:inline;}
@media (max-width: 575px){
  .btn-add{padding:.45rem .6rem;}
  .btn-add .btn-label{display:none;}
  .btn-add i{margin-right:0 !important;}
}

/* Chips area */
.chips-wrap-block{ background:#f8fafc; border:1px solid rgba(0,0,0,.06); border-radius:.5rem; padding:.5rem .75rem; margin-bottom:.75rem; }
.chips-header{ margin-bottom:.35rem; }
.chips-title{ font-weight:600; font-size:.95rem; }
.chips-wrap { display:flex; flex-wrap:wrap; gap:.5rem; padding:.25rem 0; }
.chip { display:inline-flex; align-items:center; padding:.25rem .5rem; border:1px solid rgba(0,0,0,.1); border-radius:999px; background:#fff; box-shadow:0 1px 4px rgba(0,0,0,.06); transition:all .2s ease; }
.chip:hover { transform:translateY(-2px); box-shadow:0 2px 6px rgba(0,0,0,.1); }
.chip-avatar { width:22px; height:22px; border-radius:50%; object-fit:cover; margin-right:.35rem; }
.chip-initials { display:inline-flex; align-items:center; justify-content:center; background:#f0f2f5; font-size:.7rem; font-weight:700; color:#555; }
.chip-label { max-width:160px; font-size:.85rem; white-space:nowrap; overflow:hidden; text-overflow:ellipsis; }
.chip-remove { margin-left:.35rem; border:0; background:transparent; color:#999; line-height:1; font-size:1rem; padding:0 .2rem; cursor:pointer; transition:color .2s ease; }
.chip-remove:hover { color:#d9534f; background:rgba(217,83,79,.1); border-radius:50%; }
.contact-chips{display:flex;gap:.35rem;flex-wrap:wrap;}
.chip-contact{
  display:inline-flex;align-items:center;gap:.3rem;
  padding:.25rem .5rem;
  border-radius:999px;
  font-size:.75rem;
  font-weight:600;
  line-height:1;
  border:1px solid transparent;
}
.chip-username{background:#eef2ff;color:#4338ca;border-color:#e0e7ff;}
.chip-email{background:#ecfdf3;color:#166534;border-color:#d1fadf;}
.chip-phone{background:#eff6ff;color:#1d4ed8;border-color:#dbeafe;}
.chips-scroll{flex-wrap:nowrap;overflow-x:auto;max-width:100%;-webkit-overflow-scrolling:touch;}

/* Dropdown (no Bootstrap JS needed) */
.row-dropdown { position:relative; top:0px; right:8px; left:5px; z-index:40; }
.row-menu-btn {
  height:36px; width:36px;
  border-radius:10px;
  background:#f8fafc;
  border:1px solid #e5e7eb;
  display:inline-flex;align-items:center;justify-content:center;
  color:#475569;
  transition:all .15s ease;
}
.row-menu-btn:hover{background:#eef2f7;color:#111827;box-shadow:0 6px 14px rgba(0,0,0,.08);}
.row-dropdown .dropdown-menu {
  z-index:1050; display:block; visibility:hidden; opacity:0; transform:translateY(10px);
  transition:opacity .18s ease, transform .18s ease;
  border-radius:10px; padding:.35rem; min-width:10rem;
  border:1px solid #e5e7eb; box-shadow:0 12px 30px rgba(15,23,42,.12);
}
.row-dropdown.show .dropdown-menu { visibility:visible; opacity:1; transform:translateY(0); }
.dropdown-item { font-size:12px; border-radius:8px; padding:.5rem .75rem; display:flex; align-items:center; gap:.35rem; }
.dropdown-item:hover{background:#f3f6fb;}

/* intl-tel-input dropdown over modals */
 
</style>
